import { LocationUsersDrawer } from "./LocationUsersDrawer";
import "./LocationsPage.scss";
import { useCallback, useEffect, useMemo, useState } from "react";
import LocationsBanner from "../../LocationsBanner";
import TeamsApi from "../../../../api/TeamsApi";
import { useRecoilState } from "recoil";
import { locationsState, memberState } from "../../../../state/atoms";
import {
  AddOnDtoTypeEnum,
  LocationDto,
  TransferFilesDto,
} from "../../../../generated/dashboard-client";
import RemoveLocationModal from "../../modal/RemoveLocationModal";
import TransferFilesToLocationModal from "../../modal/TransferFilesToLocationModal";
import AccountLocationApi from "../../../../api/AccountLocationApi";
import { success } from "../../../../service/MessageService";
import AccountsApi from "../../../../api/AccountsApi";
import EditLocationWarningModal from "../../modal/EditLocationWarningModal";
import { useNavigate } from "react-router-dom";
import { env } from "../../../../env/env";
import { Button, Input, Skeleton } from "antd";
import DSAApi from "../../../../api/DSAApi";
import LocationsApi from "../../../../api/LocationsApi";
import { isProSubscription } from "../../../../service/BillingService";
import { LocationsTable } from "./LocationsTable";
import { SearchOutlined } from "@ant-design/icons";
import { debounce } from "lodash";
import {sortLocationsByName} from "../../../../util/utils";

const { Search } = Input;

export function LocationsPage() {
  const [storeLocations, setStoreLocation] = useRecoilState(locationsState);
  const [member] = useRecoilState(memberState);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [allLocations, setAllLocations] = useState([] as LocationDto[]);
  const [filteredLocations, setFilteredLocations] = useState([] as LocationDto[]);
  const [primaryLocation, setPrimaryLocation] = useState({} as LocationDto);

  const [location, setLocation] = useState({} as LocationDto);
  const [removeLocation, setRemoveLocation] = useState(false);
  const [usersDrawerLocation, setUsersDrawerLocation] = useState<LocationDto | null>(null);
  const [transferFiles, setTransferFiles] = useState(false);
  const [editLocationWarningModal, setEditLocationWarningModal] = useState({} as LocationDto);

  const [locationUserCounts, setLocationUserCounts] = useState<Record<number, number>>({});
  const [locationScreenCounts, setLocationScreenCounts] = useState<Record<number, number>>({});

  const [searchKey, setSearchKey] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const debouncedSetSearchKey = useMemo(() => debounce((value: string) => {
    setSearchKey((value ?? "").trim());
    setCurrentPage(1);
  }, 300), []);

  const fetchLocationUserCounts = useCallback(async (locationIds: number[]) => {
    if (locationIds.length === 0) return;
    const resp = await LocationsApi.getLocationUserCounts(locationIds);
    setLocationUserCounts(resp.data);
  }, []);

  const fetchLocationScreenCounts = useCallback(async (locationIds: number[]) => {
    for (const locationId of locationIds) {
      try {
        const resp = await DSAApi.getScreensPerLocation(locationId);
        setLocationScreenCounts(prev => ({ ...prev, [locationId]: resp.data.length }));
      } catch (error) {
        console.error('Error fetching screen count for location:', locationId, error);
      }
    }
  }, []);

  const fetchLocations = useCallback(() => {
    if (!member.teamId || !member.id) return;

    setLoading(true);
    TeamsApi.getTeamLocationsWithLogos(member.teamId)
      .then((resp) => {
        const sorted = sortLocationsByName(resp.data, "asc");

        setAllLocations(sorted);
        setFilteredLocations(sorted);

        const locationIds = resp.data
          .map(location => location.internalId)
          .filter((id): id is number => !!id);

        fetchLocationUserCounts(locationIds).then();
        fetchLocationScreenCounts(locationIds);

        setLoading(false);
      }).catch(() => {
        setLoading(false);
      });
  }, [member.teamId, member.id, fetchLocationUserCounts, fetchLocationScreenCounts]);

  const fetchPrimaryLocation = useCallback(() => {
    if (!member.internalId) return;
    AccountsApi.getPrimaryUserLocation(member.internalId)
      .then((resp: any) => {
        setPrimaryLocation(resp.data);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [member.internalId]);

  useEffect(fetchLocations, [fetchLocations]);
  useEffect(fetchPrimaryLocation, [fetchPrimaryLocation]);

  useEffect(() => {
    if (!searchKey) {
      setFilteredLocations(allLocations);
    } else {
      const lowerSearch = searchKey.toLowerCase();
      setFilteredLocations(
        allLocations.filter(loc =>
          loc.name?.toLowerCase().includes(lowerSearch) ||
          loc.address?.toLowerCase().includes(lowerSearch) ||
          loc.city?.toLowerCase().includes(lowerSearch) ||
          loc.state?.toLowerCase().includes(lowerSearch)
        )
      );
    }
  }, [searchKey, allLocations]);

  function addNewLocation() {
    if (!isProSubscription(member) && !member.diningRDIntegration) {
      window.MHM.ProModal.switchToProWithOverview({ memberId: member.id, onSuccess: () => window.location.reload() });
    } else {
      window.MHM.ProModal.addLocation({
        memberId: member.id, onSuccess: () => {
          if (member.diningRDIntegration) {
            window.location.reload();
          } else {
            fetchLocations();
          }
        }
      });
    }
  }

  function onEdit(location: LocationDto) {
    setEditLocationWarningModal(location);
  }

  function onEditLocationWarningContinue(location: LocationDto) {
    navigate(env.dashboardPath + `/account/locations/${location.id}`);
  }

  function onRemove(location: LocationDto) {
    setLocation(location);
    setRemoveLocation(true);
  }

  function onRemoveAndTransferFiles() {
    setRemoveLocation(false);
    setTransferFiles(true);
  }

  async function onRemoveAndDeleteFiles(location: LocationDto) {
    await AccountsApi.removeSubscriptionAddon(
      member.id,
      AddOnDtoTypeEnum.Location
    );
    await AccountLocationApi.deleteLocation(location.internalId || -1);

    onRemoveSuccess(location);
  }

  async function onTransfer(
    newLocation: LocationDto,
    currentLocation: LocationDto
  ) {
    const transferFilesDto = {
      fromLocationId: currentLocation.internalId || location.internalId,
      toLocationId: newLocation.internalId,
    } as TransferFilesDto;

    await AccountsApi.removeSubscriptionAddon(
      member.id,
      AddOnDtoTypeEnum.Location
    );
    await AccountLocationApi.transferTimberDesign(transferFilesDto);

    setTransferFiles(false);
    onRemoveSuccess(currentLocation);
  }

  function onRemoveSuccess(location: LocationDto) {
    success("Location removed!");
    setRemoveLocation(false);

    fetchLocations();
    fetchPrimaryLocation();

    setStoreLocation(
      storeLocations.filter((it) => it.internalId !== location.internalId)
    );
  }

  function onSetPrimary(location: LocationDto) {
    AccountsApi.setPrimaryUserLocation(member.internalId, location.internalId || -1)
      .then(() => {
        setPrimaryLocation(location);
        success("Primary location updated!");
      })
      .catch((err) => {
        console.error(err);
      });
  }

  function sortLocations(key: string) {
    setFilteredLocations(sortLocationsByName(filteredLocations, key));
  }

  function onSearchKeyUp(target: HTMLInputElement) {
    debouncedSetSearchKey(target.value);
  }

  const paginatedLocations = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredLocations.slice(start, start + pageSize);
  }, [filteredLocations, currentPage, pageSize]);

  return (
    <div className={"content locations-page"}>
      <LocationsBanner />

      <header>
        {member.owner && (
          <Button className={"new"} onClick={addNewLocation}>
            + New Location
          </Button>
        )}
        {allLocations.length > 0 && (
          <Search
            className={"search"}
            onKeyUp={ev => onSearchKeyUp(ev.target as HTMLInputElement)}
            placeholder="Search locations"
            prefix={<SearchOutlined />}
            addonAfter={<></>}
          />
        )}
      </header>

      {loading && <Skeleton />}

      {!loading && (
        <LocationsTable
          locations={paginatedLocations}
          primaryLocation={primaryLocation}
          disableRemove={allLocations.length === 1}
          onSortClick={sortLocations}
          onEdit={onEdit}
          onRemove={onRemove}
          onSetPrimary={onSetPrimary}
          locationUserCounts={locationUserCounts}
          onUsersClick={setUsersDrawerLocation}
          pagination={{
            current: currentPage,
            pageSize,
            total: filteredLocations.length,
            onChange: (page: number, size: number) => {
              setCurrentPage(page);
              setPageSize(size);
            }
          }}
        />
      )}

      {usersDrawerLocation && <LocationUsersDrawer
        location={usersDrawerLocation}
        onClose={() => setUsersDrawerLocation(null)}
      />}

      <RemoveLocationModal
        location={location}
        visible={removeLocation}
        onCancel={() => setRemoveLocation(false)}
        onDelete={onRemoveAndDeleteFiles}
        onRemoveAndTransferFiles={onRemoveAndTransferFiles}
        userCount={locationUserCounts[location.internalId!]}
        screenCount={locationScreenCounts[location.internalId || -1] || 0}
      />
      <TransferFilesToLocationModal
        visible={transferFiles}
        location={location}
        locations={allLocations.filter(
          (loc) => loc.internalId !== location.internalId
        )}
        onCancel={() => setTransferFiles(false)}
        onTransfer={onTransfer}
      />
      <EditLocationWarningModal
        visible={!!editLocationWarningModal.id}
        location={editLocationWarningModal}
        onCancel={() => setEditLocationWarningModal({})}
        onContinue={onEditLocationWarningContinue}
      />
    </div>
  );
}
